import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useClub } from '../App';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  Trophy, Users, Swords, BarChart3, Plus, X, Search, Zap,
  CheckCircle2, Circle, ArrowLeft, ToggleLeft, ToggleRight, RefreshCw,
  Link2, Shuffle, UserX, UserCheck, Wand2, Settings, GitBranch
} from 'lucide-react';
import { RatingBadge } from '../components/RatingBadge';
import TournamentBracket from '../components/TournamentBracket';
import AuditTrailPanel from '../components/AuditTrailPanel';

const TABS = [
  { id: 'Overview', label: 'Overview', Icon: Settings },
  { id: 'Players', label: 'Players', Icon: Users },
  { id: 'Games', label: 'Games', Icon: Swords },
  { id: 'Bracket', label: 'Bracket', Icon: GitBranch },
  { id: 'Standings', label: 'Standings', Icon: BarChart3 },
  { id: 'Playoffs', label: 'Playoffs', Icon: Trophy },
];

const GAME_TYPE_LABELS = {
  singles: 'Singles (1v1)',
  doubles_changing: 'Doubles - Rotating Partners',
  doubles_fixed_assigned: 'Doubles - Fixed Partners (Assigned)',
  doubles_fixed_random: 'Doubles - Fixed Partners (Random)',
};

const RATING_MODE_LABELS = {
  lifetime: 'Lifetime Ratings',
  tournament_isolated: 'Tournament Only',
};

// ─── Partner Pairs Management Modal ───
function PartnerPairsModal({ tournament, players, onClose, onSaved }) {
  const [suggestedPairs, setSuggestedPairs] = useState([]);
  const [unpaired, setUnpaired] = useState(null);
  const [currentPairs, setCurrentPairs] = useState(tournament.partner_pairs || []);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manualPlayer1, setManualPlayer1] = useState('');
  const [manualPlayer2, setManualPlayer2] = useState('');

  const fetchSuggestions = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/tournaments/${tournament.id}/partners/suggest`);
      setSuggestedPairs(res.data.suggested_pairs || []);
      setUnpaired(res.data.unpaired_player);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to get suggestions');
    } finally { setLoading(false); }
  };

  const applyRandom = async () => {
    setLoading(true);
    try {
      await api.post(`/tournaments/${tournament.id}/partners/random`);
      toast.success('Random partners assigned');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign random partners');
    } finally { setLoading(false); }
  };

  const applySuggested = async () => {
    if (suggestedPairs.length === 0) {
      toast.error('No suggested pairs to apply');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/tournaments/${tournament.id}/partners`, {
        pairs: suggestedPairs.map(p => ({ player1_id: p.player1_id, player2_id: p.player2_id }))
      });
      toast.success('Suggested partners applied');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply partners');
    } finally { setSaving(false); }
  };

  const addManualPair = async () => {
    if (!manualPlayer1 || !manualPlayer2 || manualPlayer1 === manualPlayer2) {
      toast.error('Select two different players');
      return;
    }
    const newPairs = [...currentPairs, { player1_id: manualPlayer1, player2_id: manualPlayer2 }];
    setSaving(true);
    try {
      await api.post(`/tournaments/${tournament.id}/partners`, { pairs: newPairs });
      toast.success('Partner pair added');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add pair');
    } finally { setSaving(false); }
  };

  const pairedPlayerIds = new Set();
  currentPairs.forEach(p => { pairedPlayerIds.add(p.player1_id); pairedPlayerIds.add(p.player2_id); });
  const availableForPairing = players.filter(p => !pairedPlayerIds.has(p.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 sticky top-0 bg-white">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Link2 className="inline w-5 h-5 mr-2 text-green-600" />
            Manage Partner Pairs
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="close-partners-modal"><X size={18} /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Current Pairs */}
          <div>
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Current Pairs ({currentPairs.length})</h4>
            {currentPairs.length === 0 ? (
              <p className="text-sm text-slate-400">No pairs assigned yet</p>
            ) : (
              <div className="space-y-2">
                {currentPairs.map((pair, i) => (
                  <div key={i} className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                    <span className="text-sm font-medium text-green-700">{pair.player1_name || pair.player1_id}</span>
                    <Link2 size={14} className="text-green-400" />
                    <span className="text-sm font-medium text-green-700">{pair.player2_name || pair.player2_id}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Auto-assign buttons */}
          <div className="flex gap-3">
            <button
              onClick={fetchSuggestions}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-all disabled:opacity-50"
              data-testid="suggest-pairs-btn"
            >
              <Wand2 size={14} /> {loading ? 'Loading...' : 'Suggest Balanced Pairs'}
            </button>
            <button
              onClick={applyRandom}
              disabled={loading}
              className="flex-1 flex items-center justify-center gap-2 h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-all disabled:opacity-50"
              data-testid="random-pairs-btn"
            >
              <Shuffle size={14} /> Random Pairs
            </button>
          </div>

          {/* Suggested Pairs */}
          {suggestedPairs.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Suggested Pairs (Balanced by Rating)</h4>
              <div className="space-y-2 mb-3">
                {suggestedPairs.map((pair, i) => (
                  <div key={i} className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <span className="text-sm font-medium text-amber-700">{pair.player1_name} ({pair.player1_rating})</span>
                    <Link2 size={14} className="text-amber-400" />
                    <span className="text-sm font-medium text-amber-700">{pair.player2_name} ({pair.player2_rating})</span>
                    <span className="ml-auto text-xs text-amber-600 font-mono">={pair.combined_rating}</span>
                  </div>
                ))}
                {unpaired && (
                  <p className="text-xs text-amber-600">Note: {unpaired.name} has no partner (odd number of players)</p>
                )}
              </div>
              <button
                onClick={applySuggested}
                disabled={saving}
                className="w-full h-9 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50"
                data-testid="apply-suggested-btn"
              >
                {saving ? 'Applying...' : 'Apply Suggested Pairs'}
              </button>
            </div>
          )}

          {/* Manual Add */}
          {availableForPairing.length >= 2 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2">Add Manual Pair</h4>
              <div className="flex gap-2 items-end">
                <select
                  value={manualPlayer1}
                  onChange={e => setManualPlayer1(e.target.value)}
                  className="flex-1 h-10 border border-slate-200 rounded-lg px-3 text-sm"
                  data-testid="manual-player1-select"
                >
                  <option value="">Select Player 1</option>
                  {availableForPairing.map(p => <option key={p.id} value={p.id}>{p.display_name || p.name}</option>)}
                </select>
                <Link2 size={14} className="text-slate-300 flex-shrink-0" />
                <select
                  value={manualPlayer2}
                  onChange={e => setManualPlayer2(e.target.value)}
                  className="flex-1 h-10 border border-slate-200 rounded-lg px-3 text-sm"
                  data-testid="manual-player2-select"
                >
                  <option value="">Select Player 2</option>
                  {availableForPairing.filter(p => p.id !== manualPlayer1).map(p => <option key={p.id} value={p.id}>{p.display_name || p.name}</option>)}
                </select>
                <button
                  onClick={addManualPair}
                  disabled={saving || !manualPlayer1 || !manualPlayer2}
                  className="h-10 px-4 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50"
                  data-testid="add-manual-pair-btn"
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 pb-4 sticky bottom-0 bg-white border-t border-slate-100 pt-3">
          <button onClick={onClose} className="w-full h-10 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-all" data-testid="done-partners-btn">Done</button>
        </div>
      </div>
    </div>
  );
}

// ─── Rating Info Panel (shows rating changes + ELO calculation detail) ───
function RatingInfoPanel({ gameId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/games/${gameId}/rating-info`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [gameId]);

  const RatingChange = ({ label, info }) => {
    if (!info) return null;
    const color = info.change > 0 ? 'text-green-600' : info.change < 0 ? 'text-red-500' : 'text-slate-400';
    const sign = info.change > 0 ? '+' : '';
    return (
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-slate-500 w-16">{label}</span>
        <div className="flex items-center gap-1.5 font-mono">
          <span className="text-slate-400">{info.before}</span>
          <span className="text-slate-300">→</span>
          <span className="font-bold text-slate-700">{info.after}</span>
          <span className={`font-bold min-w-[32px] text-right ${color}`}>{sign}{info.change}</span>
        </div>
      </div>
    );
  };

  // Derive ELO calculation details from the players data
  const eloDetails = (() => {
    if (!data?.players?.length) return null;
    const winners = data.players.filter(p => p.result === 'win');
    const losers = data.players.filter(p => p.result === 'loss');
    if (!winners.length || !losers.length) return null;

    const winAvg = Math.round(winners.reduce((s, p) => s + (p.tournament_rating?.before ?? 1500), 0) / winners.length);
    const loseAvg = Math.round(losers.reduce((s, p) => s + (p.tournament_rating?.before ?? 1500), 0) / losers.length);
    const expectedWin = 1 / (1 + Math.pow(10, (loseAvg - winAvg) / 400));
    const expectedLoss = 1 - expectedWin;

    // Derive K from the actual change of a sample winner
    const sampleChange = winners[0]?.tournament_rating?.change;
    const kFactor = sampleChange != null && Math.abs(1 - expectedWin) > 0.001
      ? Math.round(sampleChange / (1 - expectedWin))
      : 32;

    const winChange = Math.round(kFactor * (1 - expectedWin));
    const lossChange = Math.round(kFactor * (0 - expectedWin));

    return { winAvg, loseAvg, expectedWin, expectedLoss, kFactor, winChange, lossChange };
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40" data-testid={`rating-info-${gameId}`}>
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-sm max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-shrink-0">
          <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Rating Info</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="close-rating-info"><X size={16} /></button>
        </div>

        <div className="overflow-y-auto flex-1 px-4 py-3 space-y-3">
          {loading ? (
            <div className="py-6 text-center text-sm text-slate-400">Loading...</div>
          ) : !data?.players?.length ? (
            <div className="py-6 text-center text-sm text-slate-400">No rating data available</div>
          ) : (
            <>
              {/* ELO Calculation Breakdown */}
              {eloDetails && (
                <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 space-y-2">
                  <p className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">ELO Calculation</p>

                  {/* Team ratings */}
                  <div className="space-y-0.5 text-[10px]">
                    <div className="flex justify-between">
                      <span className="text-slate-500">Win team avg</span>
                      <span className="font-mono font-bold text-green-700">{eloDetails.winAvg}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Loss team avg</span>
                      <span className="font-mono font-bold text-red-500">{eloDetails.loseAvg}</span>
                    </div>
                  </div>

                  {/* Expected probability */}
                  <div className="border-t border-slate-200 pt-2 space-y-0.5 text-[10px]">
                    <p className="text-[9px] text-slate-400 font-mono">Expected = 1 ÷ (1 + 10^((opp − self) ÷ 400))</p>
                    <p className="text-[9px] text-slate-400 font-mono">
                      Win: 1 ÷ (1 + 10^(({eloDetails.loseAvg} − {eloDetails.winAvg}) ÷ 400))
                    </p>
                    <div className="flex justify-between mt-1">
                      <span className="text-slate-500">Win team expected</span>
                      <span className="font-mono font-bold text-green-600">{(eloDetails.expectedWin * 100).toFixed(1)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Loss team expected</span>
                      <span className="font-mono font-bold text-red-500">{(eloDetails.expectedLoss * 100).toFixed(1)}%</span>
                    </div>
                  </div>

                  {/* K × (actual − expected) */}
                  <div className="border-t border-slate-200 pt-2 space-y-0.5 text-[10px]">
                    <p className="text-[9px] text-slate-400">Change = K × (actual − expected)</p>
                    <div className="flex justify-between">
                      <span className="text-slate-500">K-factor</span>
                      <span className="font-mono font-bold text-slate-700">{eloDetails.kFactor}</span>
                    </div>
                    <div className="flex justify-between text-green-600">
                      <span className="font-mono text-[9px]">{eloDetails.kFactor} × (1.000 − {eloDetails.expectedWin.toFixed(3)})</span>
                      <span className="font-bold">+{eloDetails.winChange}</span>
                    </div>
                    <div className="flex justify-between text-red-500">
                      <span className="font-mono text-[9px]">{eloDetails.kFactor} × (0.000 − {eloDetails.expectedWin.toFixed(3)})</span>
                      <span className="font-bold">{eloDetails.lossChange}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Per-player changes */}
              {data.players.map((p) => (
                <div key={p.player_id} className={`rounded-lg border p-3 ${p.result === 'win' ? 'border-green-200 bg-green-50/50' : 'border-red-200 bg-red-50/30'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-slate-800">{p.player_name}</span>
                    <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${p.result === 'win' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>{p.result}</span>
                  </div>
                  <div className="space-y-1">
                    <RatingChange label="Tournament" info={p.tournament_rating} />
                    <RatingChange label="Club" info={p.club_rating} />
                    <RatingChange label="Lifetime" info={p.lifetime_rating} />
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="px-4 py-2 border-t border-slate-100 text-[9px] text-slate-400 flex-shrink-0">
          ELO: winner gains points the lower-rated team expected to get; loser loses those same points.
        </div>
      </div>
    </div>
  );
}

// ─── Score Entry Modal (Full-screen) ───
function ScoreEntryModal({ game, onSave, onCancel }) {
  const [t1, setT1] = useState(game.team1_score != null ? String(game.team1_score) : '');
  const [t2, setT2] = useState(game.team2_score != null ? String(game.team2_score) : '');
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    const s1 = parseInt(t1);
    const s2 = parseInt(t2);
    if (isNaN(s1) || isNaN(s2) || s1 < 0 || s2 < 0) {
      toast.error('Enter valid scores');
      return;
    }
    if (s1 === s2) {
      toast.error('Game cannot end in a tie');
      return;
    }
    setLoading(true);
    try {
      await api.put(`/games/${game.id}/score`, { team1_score: s1, team2_score: s2 });
      toast.success('Game completed! Ratings updated.');
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save score');
    } finally {
      setLoading(false);
    }
  };

  const PlayerAvatar = ({ name, idx }) => (
    <div className={`w-12 h-12 rounded-full border-2 border-white shadow flex items-center justify-center text-white font-bold text-sm flex-shrink-0 ${
      ['bg-emerald-500', 'bg-sky-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500', 'bg-teal-500'][idx % 6]
    }`} style={{ marginLeft: idx > 0 ? '-8px' : '0', zIndex: 4 - idx }}>
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-white flex flex-col" data-testid={`score-modal-${game.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4">
        <button onClick={onCancel} className="flex items-center gap-1.5 text-red-500 font-medium text-sm" data-testid="cancel-score-modal">
          <ArrowLeft size={16} /> Cancel
        </button>
        <span className="text-xs text-slate-400">Higher score wins</span>
      </div>

      {/* Court & Game label */}
      <div className="text-center mb-2">
        <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider">
          {game.court_number ? `Court ${game.court_number}` : 'Game'}
        </p>
        <h2 className="text-xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {game.status === 'completed' ? 'Edit Score' : 'Add Score'}
        </h2>
      </div>

      {/* Teams display */}
      <div className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-md">
          {/* Teams row */}
          <div className="flex items-start justify-between mb-8">
            {/* Team 1 */}
            <div className="flex-1 flex flex-col items-center text-center">
              <div className="flex justify-center mb-3">
                {game.team1_players.map((name, i) => (
                  <PlayerAvatar key={i} name={name} idx={i} />
                ))}
              </div>
              {game.team1_players.map((name, i) => (
                <p key={i} className="text-sm font-medium text-slate-700 leading-tight max-w-[120px] truncate">{name}</p>
              ))}
            </div>

            {/* VS */}
            <div className="flex-shrink-0 px-4 pt-3">
              <span className="text-sm font-bold text-slate-300 tracking-widest">VS</span>
            </div>

            {/* Team 2 */}
            <div className="flex-1 flex flex-col items-center text-center">
              <div className="flex justify-center mb-3">
                {game.team2_players.map((name, i) => (
                  <PlayerAvatar key={i} name={name} idx={i} />
                ))}
              </div>
              {game.team2_players.map((name, i) => (
                <p key={i} className="text-sm font-medium text-slate-700 leading-tight max-w-[120px] truncate">{name}</p>
              ))}
            </div>
          </div>

          {/* Score inputs */}
          <div className="flex items-center gap-4 justify-center mb-8">
            <input
              type="number"
              min={0}
              value={t1}
              onChange={e => setT1(e.target.value)}
              placeholder="0"
              className="w-24 h-20 rounded-2xl bg-slate-100 border-2 border-slate-200 text-3xl font-bold font-mono text-center text-slate-900 focus:outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 transition-all"
              data-testid={`score-team1-${game.id}`}
              autoFocus
            />
            <span className="text-2xl font-bold text-slate-300">-</span>
            <input
              type="number"
              min={0}
              value={t2}
              onChange={e => setT2(e.target.value)}
              placeholder="0"
              className="w-24 h-20 rounded-2xl bg-slate-100 border-2 border-slate-200 text-3xl font-bold font-mono text-center text-slate-900 focus:outline-none focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 transition-all"
              data-testid={`score-team2-${game.id}`}
            />
          </div>

          {/* Save button */}
          <button
            onClick={handleSave}
            disabled={loading || !t1 || !t2}
            className="w-full h-12 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 disabled:from-slate-300 disabled:to-slate-300 text-white text-base font-bold shadow-lg shadow-emerald-200 transition-all active:scale-[0.98]"
            data-testid={`save-score-${game.id}`}
          >
            {loading ? 'Saving...' : 'Save Score'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Game Card ───────────────────────────────────────────────────────────────
const AVATAR_COLORS = ['bg-emerald-500', 'bg-sky-500', 'bg-amber-500', 'bg-violet-500', 'bg-rose-500', 'bg-teal-500'];

function GameCard({ game, canManage, onScoreSaved }) {
  const [showScoreModal, setShowScoreModal] = useState(false);
  const [showRatingInfo, setShowRatingInfo] = useState(false);
  const isCompleted = game.status === 'completed';
  const t1Won = isCompleted && game.team1_score > game.team2_score;
  const t2Won = isCompleted && game.team2_score > game.team1_score;

  const Av = ({ name, idx }) => (
    <div
      className={`w-7 h-7 rounded-full border-2 border-white flex items-center justify-center text-white font-bold text-[10px] flex-shrink-0 ${AVATAR_COLORS[idx % AVATAR_COLORS.length]}`}
      style={{ marginLeft: idx > 0 ? '-5px' : '0', zIndex: 4 - idx }}
    >
      {name?.[0]?.toUpperCase() || '?'}
    </div>
  );

  return (
    <>
      <div
        className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all
          ${isCompleted ? 'border-slate-200' : 'border-emerald-300'}
          ${canManage ? 'cursor-pointer active:scale-[0.99]' : ''}`}
        onClick={() => canManage && setShowScoreModal(true)}
        data-testid={`game-card-${game.id}`}
      >
        {/* 3-column layout: Team1 | Score/Btn | Team2 */}
        <div className="flex items-stretch min-h-[56px]">
          {/* Team 1 */}
          <div className={`flex-1 p-2 min-w-0 ${t2Won ? 'opacity-40' : ''}`}>
            <div className="flex items-center gap-0.5 mb-1">
              {game.team1_players.map((n, i) => <Av key={i} name={n} idx={i} />)}
              {t1Won && <span className="ml-1 text-[9px] text-emerald-600 font-bold">▶</span>}
            </div>
            {game.team1_players.map((n, i) => (
              <p key={i} className="text-[10px] font-semibold text-slate-800 leading-tight truncate">{n}</p>
            ))}
          </div>

          {/* Center */}
          <div className="flex-shrink-0 w-[70px] flex items-center justify-center bg-slate-50 border-x border-slate-100">
            {isCompleted ? (
              <div className="text-center" data-testid={`score-display-${game.id}`}>
                <span className={`text-sm font-bold font-mono ${t1Won ? 'text-emerald-600' : 'text-slate-400'}`}>{game.team1_score}</span>
                <span className="text-slate-300 text-xs mx-0.5">-</span>
                <span className={`text-sm font-bold font-mono ${t2Won ? 'text-emerald-600' : 'text-slate-400'}`}>{game.team2_score}</span>
              </div>
            ) : (
              <div className="bg-emerald-500 rounded-full px-2 py-1">
                <span className="text-[9px] font-bold text-white uppercase block text-center leading-tight">
                  {canManage ? 'Score' : 'Open'}
                </span>
              </div>
            )}
          </div>

          {/* Team 2 */}
          <div className={`flex-1 p-2 min-w-0 text-right ${t1Won ? 'opacity-40' : ''}`}>
            <div className="flex items-center justify-end gap-0.5 mb-1">
              {t2Won && <span className="mr-1 text-[9px] text-emerald-600 font-bold">◀</span>}
              {game.team2_players.map((n, i) => <Av key={i} name={n} idx={i + 2} />)}
            </div>
            {game.team2_players.map((n, i) => (
              <p key={i} className="text-[10px] font-semibold text-slate-800 leading-tight truncate">{n}</p>
            ))}
          </div>
        </div>

        {/* Footer for completed games */}
        {isCompleted && (
          <div className="border-t border-slate-100 flex items-stretch bg-slate-50">
            <div className="flex-1 px-2 py-1 flex items-center">
              <span className={`text-[9px] font-bold ${t1Won ? 'text-emerald-600' : t2Won ? 'text-red-500' : 'text-slate-500'}`}>
                {t1Won ? 'Win' : t2Won ? 'Loss' : 'Draw'}
              </span>
            </div>
            <div className="flex-shrink-0 w-[70px] flex items-center justify-center border-x border-slate-100 py-1">
              <button
                onClick={e => { e.stopPropagation(); setShowRatingInfo(true); }}
                className="text-[9px] text-slate-400 hover:text-green-600 flex items-center gap-0.5"
                data-testid={`rating-info-btn-${game.id}`}
              >
                <BarChart3 size={8} /> Info
              </button>
            </div>
            <div className="flex-1 px-2 py-1 flex items-center justify-end">
              <span className={`text-[9px] font-bold ${t2Won ? 'text-emerald-600' : t1Won ? 'text-red-500' : 'text-slate-500'}`}>
                {t2Won ? 'Win' : t1Won ? 'Loss' : 'Draw'}
              </span>
            </div>
          </div>
        )}
      </div>

      {showScoreModal && (
        <ScoreEntryModal game={game} onSave={() => { setShowScoreModal(false); onScoreSaved(); }} onCancel={() => setShowScoreModal(false)} />
      )}
      {showRatingInfo && (
        <RatingInfoPanel gameId={game.id} onClose={() => setShowRatingInfo(false)} />
      )}
    </>
  );
}

// ─── Court Grouped Games ───
function CourtGroupedGames({ games, canManage, onScoreSaved }) {
  // Group games by court_number
  const grouped = {};
  games.forEach(g => {
    const court = g.court_number || 0;
    if (!grouped[court]) grouped[court] = [];
    grouped[court].push(g);
  });
  const courts = Object.keys(grouped).sort((a, b) => Number(a) - Number(b));

  return (
    <div className="space-y-6">
      {courts.map(court => (
        <div key={court} data-testid={`court-group-${court}`}>
          {Number(court) > 0 && (
            <div className="flex items-center gap-3 mb-3">
              <div className="h-px bg-slate-200 flex-1" />
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Court {court}</span>
              <div className="h-px bg-slate-200 flex-1" />
            </div>
          )}
          <div className="space-y-3">
            {grouped[court].map(g => (
              <GameCard key={g.id} game={g} canManage={canManage} onScoreSaved={onScoreSaved} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Playoffs Tab ───
function PlayoffsTab({ tournamentId, tournament, canManage, onRefresh }) {
  const [playoff, setPlayoff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [generating, setGenerating] = useState(false);

  const fetchPlayoff = () => {
    api.get(`/tournaments/${tournamentId}/playoffs`)
      .then(r => setPlayoff(r.data.playoff))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPlayoff(); }, [tournamentId]);

  const createPlayoff = async () => {
    setCreating(true);
    try {
      await api.post(`/tournaments/${tournamentId}/playoffs/create`, { name: 'Playoff' });
      toast.success('Playoff created!');
      fetchPlayoff();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create playoff');
    } finally { setCreating(false); }
  };

  const generatePlayoffGames = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/tournaments/${tournamentId}/playoffs/generate-games`);
      toast.success(res.data.message);
      fetchPlayoff();
      onRefresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate playoff games');
    } finally { setGenerating(false); }
  };

  if (loading) return <div className="py-8 text-center text-xs text-slate-400">Loading...</div>;

  if (!playoff) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <Trophy className="w-8 h-8 text-amber-400 mx-auto mb-2" />
        <h3 className="font-bold text-slate-900 text-base mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>No Playoff Yet</h3>
        <p className="text-xs text-slate-500 mb-4 max-w-xs mx-auto">
          Create a playoff to seed players into groups by tournament rating across {tournament?.num_courts || 2} courts.
        </p>
        {canManage && (
          <button onClick={createPlayoff} disabled={creating}
            className="h-9 px-5 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg transition-all"
            data-testid="create-playoff-btn">
            {creating ? 'Creating...' : 'Create Playoff'}
          </button>
        )}
      </div>
    );
  }

  // Compute overall playoff standings from all completed games across all groups
  const allPlayoffGames = playoff.groups?.flatMap(g => g.games || []) || [];
  const completedPlayoffGames = allPlayoffGames.filter(g => g.status === 'completed');
  const playerRecord = {};
  completedPlayoffGames.forEach(g => {
    const t1Won = g.team1_score > g.team2_score;
    const winners = t1Won ? (g.team1_players || []) : (g.team2_players || []);
    const losers = !t1Won ? (g.team1_players || []) : (g.team2_players || []);
    winners.forEach(n => { playerRecord[n] = playerRecord[n] || { w: 0, l: 0 }; playerRecord[n].w++; });
    losers.forEach(n => { playerRecord[n] = playerRecord[n] || { w: 0, l: 0 }; playerRecord[n].l++; });
  });
  const sortedLeaders = Object.entries(playerRecord)
    .sort((a, b) => b[1].w - a[1].w || a[1].l - b[1].l)
    .slice(0, 3);

  return (
    <div className="space-y-3" data-testid="playoff-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>{playoff.name}</h2>
          <p className="text-[10px] text-slate-500">{playoff.groups?.length || 0} groups · {allPlayoffGames.length} games</p>
        </div>
        {canManage && (
          <button onClick={generatePlayoffGames} disabled={generating}
            className="flex items-center gap-1.5 h-8 px-3 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg transition-all"
            data-testid="generate-playoff-games-btn">
            <Zap size={12} />
            {generating ? 'Generating...' : 'Generate Games'}
          </button>
        )}
      </div>

      {/* Playoff leaders podium */}
      {completedPlayoffGames.length > 0 && sortedLeaders.length >= 1 && (
        <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 rounded-xl border border-amber-200 p-4" data-testid="playoff-podium">
          <div className="flex items-center gap-1.5 justify-center mb-3">
            <Trophy size={13} className="text-amber-600" />
            <span className="text-xs font-bold text-amber-800">Playoff Leaders</span>
          </div>
          <div className="flex items-end justify-center gap-3">
            {[sortedLeaders[1], sortedLeaders[0], sortedLeaders[2]].map((entry, i) => {
              if (!entry) return <div key={i} className="w-14" />;
              const [name, rec] = entry;
              const podiumH = ['h-16', 'h-20', 'h-14'];
              const podiumBg = ['bg-slate-200', 'bg-gradient-to-b from-amber-300 to-amber-400', 'bg-amber-200'];
              const ranks = [2, 1, 3];
              const rankBg = ['bg-slate-400', 'bg-amber-400', 'bg-amber-700'];
              const avatarCls = ['bg-slate-100 border border-slate-300', 'bg-amber-100 border-2 border-amber-400', 'bg-amber-50 border border-amber-300'];
              return (
                <div key={i} className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full ${avatarCls[i]} flex items-center justify-center font-bold text-xs text-slate-700 mb-1`}>
                    {name[0]?.toUpperCase() || '?'}
                  </div>
                  <div className={`${podiumBg[i]} ${podiumH[i]} rounded-t-lg flex flex-col items-center justify-end pb-1.5 px-2`} style={{ minWidth: '54px' }}>
                    <span className={`w-4 h-4 rounded-full ${rankBg[i]} text-white text-[9px] font-bold flex items-center justify-center mb-0.5`}>{ranks[i]}</span>
                    <p className="text-[9px] font-bold text-center leading-tight max-w-[50px] truncate">{name}</p>
                    <p className="text-[8px] text-slate-600 font-mono">{rec.w}W·{rec.l}L</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Playoff groups */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {playoff.groups?.map(group => {
          const groupCompleted = (group.games || []).filter(g => g.status === 'completed');
          const groupWins = {};
          groupCompleted.forEach(g => {
            const t1Won = g.team1_score > g.team2_score;
            (t1Won ? g.team1_players : g.team2_players)?.forEach(n => {
              groupWins[n] = (groupWins[n] || 0) + 1;
            });
          });
          const groupWinner = Object.entries(groupWins).sort((a, b) => b[1] - a[1])[0];

          return (
            <div key={group.group_name} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`playoff-group-${group.group_name}`}>
              <div className="px-3 py-2 border-b border-slate-100 bg-amber-50 flex items-center justify-between">
                <span className="text-xs font-bold text-amber-800">{group.group_name}</span>
                <span className="text-[10px] text-amber-600">
                  Court{group.courts?.length > 1 ? 's' : ''} {group.courts?.join(', ') || group.court}
                </span>
              </div>

              {/* Players */}
              <div className="px-3 py-2 border-b border-slate-100">
                <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">Players</p>
                <div className="space-y-1">
                  {group.players?.map((p, i) => (
                    <div key={p.player_id} className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <span className="w-4 h-4 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[8px] font-bold flex-shrink-0">{i + 1}</span>
                        <span className="text-[10px] font-medium text-slate-800 truncate max-w-[100px]">{p.player_name}</span>
                      </div>
                      <span className="text-[9px] font-mono text-slate-500">{p.tournament_rating}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Games — card style with winner highlights */}
              {group.games?.length > 0 && (
                <div className="px-3 py-2">
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                    Games ({group.games.length})
                    {groupCompleted.length > 0 && <span className="ml-1 text-green-600">{groupCompleted.length} done</span>}
                  </p>
                  <div className="space-y-1.5">
                    {group.games.map(g => {
                      const t1Won = g.status === 'completed' && g.team1_score > g.team2_score;
                      const t2Won = g.status === 'completed' && g.team2_score > g.team1_score;
                      return (
                        <div key={g.id} className={`rounded-lg border overflow-hidden ${g.status === 'completed' ? 'border-slate-200 bg-white' : 'border-emerald-200 bg-emerald-50/40'}`}>
                          <div className="flex items-stretch min-h-[38px]">
                            <div className={`flex-1 px-2 py-1.5 min-w-0 ${t2Won ? 'opacity-40' : ''}`}>
                              <p className={`text-[9px] font-semibold leading-tight truncate ${t1Won ? 'text-emerald-700' : 'text-slate-700'}`}>
                                {t1Won && '▶ '}{g.team1_players?.join(' & ')}
                              </p>
                            </div>
                            <div className="w-12 flex items-center justify-center bg-slate-50 border-x border-slate-100 flex-shrink-0">
                              {g.status === 'completed' ? (
                                <span className="text-[10px] font-bold font-mono">
                                  <span className={t1Won ? 'text-emerald-600' : 'text-slate-400'}>{g.team1_score}</span>
                                  <span className="text-slate-300">-</span>
                                  <span className={t2Won ? 'text-emerald-600' : 'text-slate-400'}>{g.team2_score}</span>
                                </span>
                              ) : (
                                <span className="text-[9px] text-slate-400">vs</span>
                              )}
                            </div>
                            <div className={`flex-1 px-2 py-1.5 min-w-0 text-right ${t1Won ? 'opacity-40' : ''}`}>
                              <p className={`text-[9px] font-semibold leading-tight truncate ${t2Won ? 'text-emerald-700' : 'text-slate-700'}`}>
                                {g.team2_players?.join(' & ')}{t2Won && ' ◀'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Group winner badge */}
              {groupWinner && (
                <div className="px-3 py-1.5 bg-amber-50 border-t border-amber-100 flex items-center gap-1.5">
                  <Trophy size={10} className="text-amber-500 flex-shrink-0" />
                  <span className="text-[9px] text-amber-700 font-semibold truncate">{groupWinner[0]} · {groupWinner[1]}W</span>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Add Players Modal ───
function AddPlayersModal({ tournamentId, existingPlayerIds, groupId, onClose, onAdded }) {
  const [allPlayers, setAllPlayers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const fetchPlayers = () => {
    api.get(`/players?group_id=${groupId}`).then(r => setAllPlayers(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchPlayers(); }, [groupId]);

  const filtered = allPlayers.filter(p => (p.name || '').toLowerCase().includes(search.toLowerCase()));

  const addPlayer = async (p) => {
    setBusy(p.id);
    try {
      await api.post(`/tournaments/${tournamentId}/players`, [p.id]);
      existingPlayerIds.push(p.id);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add');
    } finally { setBusy(null); }
  };

  const removePlayer = async (p) => {
    setBusy(p.id);
    try {
      await api.delete(`/tournaments/${tournamentId}/players/${p.id}`);
      const idx = existingPlayerIds.indexOf(p.id);
      if (idx >= 0) existingPlayerIds.splice(idx, 1);
      onAdded();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    } finally { setBusy(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Manage Players</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" data-testid="close-add-players-modal"><X size={18} /></button>
        </div>

        <div className="px-4 pt-4 pb-2">
          <div className="relative mb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
            <input
              type="text" placeholder="Search players by name..." value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full h-9 pl-8 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
              data-testid="add-players-search-input"
            />
          </div>

          <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> In ({existingPlayerIds.length})</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300 inline-block" /> Available ({allPlayers.length - existingPlayerIds.length})</span>
          </div>
          <p className="text-[11px] text-slate-400 mb-1">Only club players are shown. To create a new player, use <span className="font-medium text-slate-500">Manage Players</span> in the club's Players tab.</p>
        </div>

        <div className="max-h-80 overflow-y-auto px-4 pb-4 pt-2" data-testid="player-roster-list">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 py-2.5 animate-pulse">
                <div className="w-8 h-8 bg-slate-100 rounded-full" />
                <div className="h-3 bg-slate-100 rounded w-28 flex-1" />
                <div className="w-7 h-7 bg-slate-100 rounded-lg" />
              </div>
            ))
          ) : filtered.length === 0 ? (
            <p className="text-sm text-slate-400 py-6 text-center">
              {search ? 'No players match your search' : 'No players in this club yet. Add players from the club\u2019s Players tab first.'}
            </p>
          ) : (
            filtered.map((p, i) => {
              const isIn = existingPlayerIds.includes(p.id);
              const isBusy = busy === p.id;
              return (
                <div key={p.id} className={`flex items-center gap-3 py-2.5 ${i < filtered.length - 1 ? 'border-b border-slate-100' : ''}`} data-testid={`roster-player-${p.id}`}>
                  {p.picture_url ? (
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/players/${p.id}/picture`} alt={p.name} className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${isIn ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                      {p.name?.[0]?.toUpperCase() || '?'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${isIn ? 'text-green-700' : 'text-slate-900'}`}>{p.name}</p>
                    <p className="text-xs text-slate-400">Rating: {p.lifetime_rating}</p>
                  </div>
                  <button
                    onClick={() => isIn ? removePlayer(p) : addPlayer(p)}
                    disabled={isBusy}
                    className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold transition-all flex-shrink-0 disabled:opacity-50 ${
                      isIn
                        ? 'bg-red-50 text-red-500 hover:bg-red-100 border border-red-200'
                        : 'bg-green-50 text-green-600 hover:bg-green-100 border border-green-200'
                    }`}
                    data-testid={`toggle-player-${p.id}`}
                    title={isIn ? `Remove ${p.name}` : `Add ${p.name}`}
                  >
                    {isBusy ? '...' : isIn ? '−' : '+'}
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="px-4 pb-4">
          <button onClick={onClose} className="w-full h-9 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium rounded-lg transition-all" data-testid="done-managing-players-btn">Done</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───
export default function TournamentDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const { selectedClub } = useClub();
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [standings, setStandings] = useState([]);
  const [activeTab, setActiveTab] = useState('Overview');
  const [loading, setLoading] = useState(true);
  const [showAddPlayers, setShowAddPlayers] = useState(false);
  const [showPartnerPairs, setShowPartnerPairs] = useState(false);
  const [numGames, setNumGames] = useState(2);
  const [generating, setGenerating] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);

  const canManage = selectedClub?.my_role === 'admin' || selectedClub?.my_role === 'contributor' || user?.role === 'admin';

  const fetchAll = async () => {
    try {
      const [t, p, g, s] = await Promise.all([
        api.get(`/tournaments/${id}`),
        api.get(`/tournaments/${id}/players`),
        api.get(`/tournaments/${id}/games`),
        api.get(`/tournaments/${id}/standings`),
      ]);
      setTournament(t.data);
      setPlayers(p.data);
      setGames(g.data);
      setStandings(s.data);
      // Default numGames to num_courts
      if (t.data.num_courts) setNumGames(t.data.num_courts);
    } catch (e) {
      toast.error('Failed to load tournament');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, [id]);

  const refreshGamesAndStandings = async () => {
    const [g, s, p] = await Promise.all([
      api.get(`/tournaments/${id}/games`),
      api.get(`/tournaments/${id}/standings`),
      api.get(`/tournaments/${id}/players`),
    ]);
    setGames(g.data);
    setStandings(s.data);
    setPlayers(p.data);
  };

  const handleUpdateField = async (field) => {
    const val = parseInt(editValue);
    if (isNaN(val) || val < 1) { toast.error('Enter a valid number'); return; }
    try {
      await api.put(`/tournaments/${id}`, { [field]: val });
      toast.success('Updated');
      setEditingField(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const toggleAvailability = async (player) => {
    try {
      await api.patch(`/tournaments/${id}/players/${player.id}/availability?available=${!player.is_available}`);
      setPlayers(ps => ps.map(p => p.id === player.id ? { ...p, is_available: !p.is_available } : p));
    } catch (e) {
      toast.error('Failed to update availability');
    }
  };

  const removePlayer = async (playerId) => {
    try {
      await api.delete(`/tournaments/${id}/players/${playerId}`);
      setPlayers(ps => ps.filter(p => p.id !== playerId));
      toast.success('Player removed from tournament');
    } catch (e) {
      toast.error('Failed to remove player');
    }
  };

  const generateGames = async () => {
    const available = players.filter(p => p.is_available);
    if (available.length < 4) {
      toast.error('Need at least 4 available players to generate games');
      return;
    }
    setGenerating(true);
    try {
      const res = await api.post(`/tournaments/${id}/games/generate`, {
        num_games: numGames,
        available_player_ids: available.map(p => p.id),
      });
      toast.success(`Generated ${res.data.count} game(s)!`);
      const g = await api.get(`/tournaments/${id}/games`);
      setGames(g.data);
      setActiveTab('Games');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate games');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="h-8 bg-slate-100 rounded w-48 animate-pulse" />
        <div className="h-32 bg-slate-100 rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="max-w-5xl mx-auto text-center py-16">
        <p className="text-red-500 font-medium">Tournament not found</p>
        <Link to="/tournaments" className="text-sm text-green-600 hover:text-green-700 mt-3 inline-block">← Back</Link>
      </div>
    );
  }

  const sessionGames = selectedSession ? games.filter(g => g.session_id === selectedSession) : games;
  const openGames = sessionGames.filter(g => g.status === 'open');
  const completedGames = sessionGames.filter(g => g.status === 'completed');
  const availablePlayers = players.filter(p => p.is_available);

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Back + Header */}
      <Link to="/tournaments" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors">
        <ArrowLeft size={14} /> Back to Tournaments
      </Link>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
              <Trophy className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="tournament-name">
                {tournament.name}
              </h1>
              {tournament.description && (
                <p className="text-sm text-slate-500 mt-0.5">{tournament.description}</p>
              )}
            </div>
          </div>
          <span className={`text-xs px-3 py-1 rounded-full font-semibold flex-shrink-0 ${
            tournament.status === 'active'
              ? 'bg-green-50 text-green-700 border border-green-200'
              : 'bg-slate-100 text-slate-600 border border-slate-200'
          }`} data-testid="tournament-status">
            {tournament.status}
          </span>
        </div>

        <div className="flex gap-1.5 mt-3 overflow-x-auto pb-0.5">
          {[
            { label: 'Plyr', value: players.length, onClick: () => setActiveTab('Players'), color: 'text-slate-900', testid: 'stat-box-players' },
            { label: 'Games', value: games.length, onClick: () => setActiveTab('Games'), color: 'text-slate-900', testid: 'stat-box-games' },
            { label: 'Done', value: completedGames.length, onClick: () => setActiveTab('Games'), color: 'text-green-600', testid: 'stat-box-completed' },
          ].map(s => (
            <button key={s.label} onClick={s.onClick} data-testid={s.testid}
              className="flex-1 min-w-[56px] bg-slate-50 hover:bg-green-50 rounded-lg py-1.5 px-1 text-center transition-all">
              <p className={`text-base font-bold ${s.color}`} style={{ fontFamily: 'Manrope, sans-serif' }}>{s.value}</p>
              <p className="text-[10px] text-slate-500 leading-none mt-0.5">{s.label}</p>
            </button>
          ))}
          {editingField === 'num_courts' ? (
            <div className="flex-1 min-w-[56px] bg-green-50 rounded-lg py-1.5 px-1 text-center border-2 border-green-300">
              <input type="number" value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus
                className="w-full text-base font-bold text-center bg-transparent border-none outline-none font-mono"
                onKeyDown={e => { if (e.key === 'Enter') handleUpdateField('num_courts'); if (e.key === 'Escape') setEditingField(null); }}
                data-testid="edit-num-courts-input" />
              <p className="text-[10px] text-green-700 leading-none">↵ save</p>
            </div>
          ) : (
            <button onClick={() => { if (canManage) { setEditingField('num_courts'); setEditValue(String(tournament.num_courts)); } }}
              className={`flex-1 min-w-[56px] bg-slate-50 rounded-lg py-1.5 px-1 text-center transition-all ${canManage ? 'hover:bg-green-50 cursor-pointer' : ''}`} data-testid="stat-box-courts">
              <p className="text-base font-bold text-slate-900 font-mono">{tournament.num_courts}</p>
              <p className="text-[10px] text-slate-500 leading-none mt-0.5">Courts{canManage ? ' ✎' : ''}</p>
            </button>
          )}
        </div>
      </div>

      {/* Tabs — scrollable on mobile, icon-only on small screens */}
      <div className="bg-slate-100 p-1 rounded-xl overflow-x-auto">
        <div className="flex gap-0.5 min-w-max">
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1 px-2 sm:px-3 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                activeTab === id
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${id.toLowerCase()}`}
              title={label}
            >
              <Icon size={13} className="flex-shrink-0" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── TAB: OVERVIEW ── */}
      {activeTab === 'Overview' && (
        <div className="space-y-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
            <h2 className="text-base font-bold text-slate-900 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Tournament Details</h2>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-1">Start Date</p>
                <p className="text-slate-900 font-medium">{tournament.start_date || 'Not set'}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-1">Rating Type</p>
                <p className="text-slate-900 font-medium capitalize">{tournament.rating_mode?.replace('_', ' ') || 'Lifetime'}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-1">Status</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  tournament.status === 'active' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-slate-100 text-slate-600 border border-slate-200'
                }`}>{tournament.status}</span>
              </div>
              <div>
                <p className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-1">Courts</p>
                <p className="text-slate-900 font-mono font-bold">{tournament.num_courts} court{tournament.num_courts !== 1 ? 's' : ''}</p>
              </div>
              <div>
                <p className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-1">Game Type</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  tournament.game_type === 'singles' 
                    ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                    : tournament.game_type?.includes('fixed')
                    ? 'bg-purple-50 text-purple-700 border border-purple-200'
                    : 'bg-amber-50 text-amber-700 border border-amber-200'
                }`} data-testid="game-type-badge">
                  {GAME_TYPE_LABELS[tournament.game_type] || tournament.game_type}
                </span>
              </div>
              <div>
                <p className="text-slate-400 text-xs font-medium uppercase tracking-wide mb-1">Rating Mode</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  tournament.rating_mode === 'lifetime' 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-slate-100 text-slate-600 border border-slate-200'
                }`} data-testid="rating-mode-badge">
                  {RATING_MODE_LABELS[tournament.rating_mode] || tournament.rating_mode}
                </span>
              </div>
            </div>
          </div>

          {/* Partner Pairs Section — only for fixed partner game types */}
          {tournament.game_type?.includes('fixed') && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Link2 className="w-5 h-5 text-purple-600" />
                  <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Partner Pairs</h2>
                </div>
                {canManage && (
                  <button
                    onClick={() => setShowPartnerPairs(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-semibold rounded-lg transition-all border border-purple-200"
                    data-testid="manage-partners-btn"
                  >
                    <Settings size={12} /> Manage
                  </button>
                )}
              </div>
              <div className="p-4">
                {tournament.partner_pairs?.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {tournament.partner_pairs.map((pair, i) => (
                      <div key={i} className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-4 py-3" data-testid={`partner-pair-${i}`}>
                        <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-sm flex-shrink-0">
                          {(pair.player1_name || 'P1')[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-purple-800 truncate">{pair.player1_name || pair.player1_id}</p>
                        </div>
                        <Link2 size={16} className="text-purple-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-purple-800 truncate">{pair.player2_name || pair.player2_id}</p>
                        </div>
                        <div className="w-8 h-8 rounded-full bg-purple-200 flex items-center justify-center text-purple-700 font-bold text-sm flex-shrink-0">
                          {(pair.player2_name || 'P2')[0]}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <Link2 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-400 text-sm">No partner pairs assigned yet</p>
                    {canManage && (
                      <button onClick={() => setShowPartnerPairs(true)} className="mt-2 text-sm text-purple-600 hover:text-purple-700 font-medium">
                        Set up partners
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {tournament.recent_games?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100">
                <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Recent Games</h2>
              </div>
              {tournament.recent_games.map((g, i) => (
                <div key={g.id} className={`px-6 py-3 flex items-center gap-4 ${i < tournament.recent_games.length - 1 ? 'border-b border-slate-100' : ''}`}>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${g.status === 'open' ? 'bg-amber-50 text-amber-700 border border-amber-200' : 'bg-slate-100 text-slate-600 border border-slate-200'}`}>
                    {g.status}
                  </span>
                  <p className="text-sm text-slate-700 flex-1 truncate">
                    {g.team1_players.join(' & ')} <span className="text-slate-400">vs</span> {g.team2_players.join(' & ')}
                  </p>
                  {g.status === 'completed' && (
                    <span className="text-sm font-mono font-bold text-slate-900 flex-shrink-0">
                      {g.team1_score} – {g.team2_score}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── TAB: PLAYERS ── */}
      {activeTab === 'Players' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-600">
              <span className="font-semibold text-green-600">{availablePlayers.length}</span> of <span className="font-semibold">{players.length}</span> available
            </p>
            {canManage && (
              <button
                onClick={() => setShowAddPlayers(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-lg transition-all active:scale-95"
                data-testid="add-players-btn"
              >
                <Plus size={16} /> Manage Players
              </button>
            )}
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="tournament-players-list">
            {players.length === 0 ? (
              <div className="py-12 text-center">
                <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">No players in this tournament yet</p>
                {canManage && (
                  <button onClick={() => setShowAddPlayers(true)} className="mt-3 text-sm text-green-600 hover:text-green-700 font-medium">Add players</button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-1 py-2 text-center font-semibold text-slate-500 uppercase tracking-wide w-6">#</th>
                    <th className="px-1.5 py-2 text-left font-semibold text-slate-500 uppercase tracking-wide">Player</th>
                    <th className="px-1 py-2 text-right font-semibold text-slate-500 uppercase tracking-wide">Rtg</th>
                    <th className="px-1 py-2 text-right font-semibold text-slate-500 uppercase tracking-wide">W/L</th>
                    {canManage && <th className="px-1 py-2 text-center font-semibold text-slate-500 uppercase tracking-wide">✓</th>}
                    {canManage && <th className="px-1 py-2 w-6" />}
                  </tr>
                </thead>
                <tbody>
                  {[...players].sort((a, b) => {
                    if (b.tournament_rating !== a.tournament_rating) return b.tournament_rating - a.tournament_rating;
                    const ratioA = (a.points_against || 0) > 0 ? (a.points_for || 0) / a.points_against : (a.points_for || 0);
                    const ratioB = (b.points_against || 0) > 0 ? (b.points_for || 0) / b.points_against : (b.points_for || 0);
                    return ratioB - ratioA;
                  }).map((p, i, arr) => {
                    // Compute display rank with ties
                    const prevP = arr[i - 1];
                    const sameRating = prevP && prevP.tournament_rating === p.tournament_rating;
                    const rank = sameRating ? (p._rank || i + 1) : i + 1;
                    if (!sameRating) p._rank = rank;
                    return (
                    <tr key={p.id} className={`${i < arr.length - 1 ? 'border-b border-slate-100' : ''} hover:bg-slate-50 transition-colors ${!p.is_available ? 'opacity-40' : ''}`} data-testid={`tournament-player-row-${p.id}`}>
                      <td className="px-1 py-2 text-center font-bold text-slate-400">{rank}</td>
                      <td className="px-1.5 py-2">
                        <div className="flex items-center gap-1.5">
                          {p.picture_url ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${p.picture_url}`} alt={p.display_name || p.name} className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-5 h-5 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-semibold text-[9px] flex-shrink-0">
                              {(p.display_name || p.name)?.[0]?.toUpperCase() || '?'}
                            </div>
                          )}
                          <span className="font-medium text-slate-900 truncate max-w-[80px]">{p.display_name || p.name}</span>
                        </div>
                      </td>
                      <td className="px-1 py-2 text-right">
                        <span className="font-mono font-bold text-[10px] text-slate-900">{p.tournament_rating}</span>
                      </td>
                      <td className="px-1 py-2 text-right">
                        <span className="text-green-600 font-medium">{p.wins}</span>
                        <span className="text-slate-300 mx-px">/</span>
                        <span className="text-red-500 font-medium">{p.losses}</span>
                      </td>
                      {canManage && (
                        <td className="px-1 py-2 text-center">
                          <button
                            onClick={() => toggleAvailability(p)}
                            className={`transition-colors ${p.is_available ? 'text-green-500 hover:text-green-600' : 'text-slate-300 hover:text-slate-400'}`}
                            title={p.is_available ? 'Mark unavailable' : 'Mark available'}
                            data-testid={`availability-toggle-${p.id}`}
                          >
                            {p.is_available ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                          </button>
                        </td>
                      )}
                      {canManage && (
                        <td className="px-1 py-2 text-right">
                          <button
                            onClick={() => removePlayer(p.id)}
                            className="text-slate-300 hover:text-red-500 p-0.5 transition-colors"
                            data-testid={`remove-player-btn-${p.id}`}
                            title="Remove"
                          >
                            <X size={11} />
                          </button>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB: GAMES ── */}
      {activeTab === 'Games' && (
        <div className="space-y-6">
          {/* Session controls */}
          {canManage && (
            <div className={`rounded-xl border shadow-sm p-4 flex items-center justify-between ${
              tournament.active_session ? 'bg-green-50 border-green-200' : 'bg-white border-slate-200'
            }`} data-testid="session-controls">
              <div className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full ${tournament.active_session ? 'bg-green-500 animate-pulse' : 'bg-slate-300'}`} />
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    {tournament.active_session ? `${tournament.active_session.replace('session-', 'Session ').replace('Session session-', 'Session ')} in progress` : 'No active session'}
                  </p>
                  <p className="text-xs text-slate-500">
                    {tournament.active_session ? 'Games generated will be tagged to this session' : 'Start a session to group games by playing time'}
                  </p>
                </div>
              </div>
              {tournament.active_session ? (
                <button onClick={async () => {
                  try {
                    await api.post(`/tournaments/${id}/sessions/end`);
                    toast.success('Session ended');
                    fetchAll();
                  } catch (err) { toast.error(err.response?.data?.detail || 'Failed to end session'); }
                }} className="h-8 px-4 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold rounded-lg transition-all"
                  data-testid="end-session-btn">End Session</button>
              ) : (
                <button onClick={async () => {
                  try {
                    await api.post(`/tournaments/${id}/sessions/start`);
                    toast.success('Session started!');
                    fetchAll();
                  } catch (err) { toast.error(err.response?.data?.detail || 'Failed to start session'); }
                }} className="h-8 px-4 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-all"
                  data-testid="start-session-btn">Start Session</button>
              )}
            </div>
          )}

          {/* Sessions list — clickable filter */}
          {(tournament.sessions?.length > 0) && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-3 py-2.5" data-testid="sessions-list">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex-shrink-0">Session:</span>
                <button
                  onClick={() => setSelectedSession(null)}
                  className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-all ${
                    !selectedSession ? 'bg-green-100 border-green-400 text-green-800' : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  All
                </button>
                {tournament.sessions.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setSelectedSession(selectedSession === s.id ? null : s.id)}
                    className={`text-[10px] px-2.5 py-1 rounded-full border font-medium transition-all ${
                      selectedSession === s.id
                        ? 'bg-green-100 border-green-400 text-green-800'
                        : s.id === tournament.active_session
                        ? 'bg-green-50 border-green-300 text-green-700 hover:bg-green-100'
                        : s.ended_at
                        ? 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'
                        : 'bg-amber-50 border-amber-200 text-amber-700 hover:bg-amber-100'
                    }`}
                  >
                    {s.name}{s.game_count > 0 ? ` (${s.game_count})` : ''}
                  </button>
                ))}
              </div>
              {selectedSession && (
                <p className="text-[9px] text-slate-500 mt-1.5">
                  Showing {tournament.sessions.find(s => s.id === selectedSession)?.name} games only · <button onClick={() => setSelectedSession(null)} className="text-green-600 underline">Clear</button>
                </p>
              )}
            </div>
          )}

          {/* Fun tournament badge */}
          {tournament.is_fun && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
              <p className="text-xs font-semibold text-amber-800">Fun Tournament — tournament standings are tracked but club and lifetime ratings are not affected</p>
            </div>
          )}

          {/* Generate section — compact inline */}
          {canManage && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-3 py-2.5 flex items-center gap-2 flex-wrap">
              <Zap size={13} className="text-slate-400 flex-shrink-0" />
              <span className="text-xs font-semibold text-slate-700 flex-shrink-0">Generate</span>
              <select
                value={numGames}
                onChange={e => setNumGames(parseInt(e.target.value))}
                className="h-8 rounded-lg border border-slate-200 px-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-green-500 bg-white"
                data-testid="num-games-select"
              >
                {[1,2,3,4,5,6,8,10].map(n => <option key={n} value={n}>{n}g</option>)}
              </select>
              <span className={`text-xs flex-shrink-0 ${availablePlayers.length >= 4 ? 'text-green-600' : 'text-red-500'}`}>
                {availablePlayers.length}/{players.length} avail
              </span>
              <button
                onClick={generateGames}
                disabled={generating || availablePlayers.length < 4}
                className="flex items-center gap-1.5 h-8 px-3 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-300 text-white text-xs font-semibold rounded-lg transition-all active:scale-95 flex-shrink-0"
                data-testid="generate-games-btn"
              >
                <Zap size={12} />
                {generating ? '...' : 'Generate'}
              </button>
              {availablePlayers.length < 4 && (
                <p className="text-[10px] text-amber-600 w-full">Need 4+ available players (toggle in Players tab)</p>
              )}
            </div>
          )}

          {/* Open games grouped by court */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Open Games <span className="text-slate-400 font-normal">({openGames.length})</span>
              </h2>
              <button onClick={refreshGamesAndStandings} className="text-slate-400 hover:text-green-600 p-1.5 hover:bg-slate-50 rounded-lg transition-colors" title="Refresh" data-testid="refresh-games-btn">
                <RefreshCw size={14} />
              </button>
            </div>
            {openGames.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
                <Circle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-400 text-sm">No open games. Generate some above!</p>
              </div>
            ) : (
              <CourtGroupedGames games={openGames} canManage={canManage} onScoreSaved={refreshGamesAndStandings} />
            )}
          </div>

          {/* Bench Players */}
          {(() => {
            const playingIds = new Set();
            openGames.forEach(g => {
              (g.team1_player_ids || []).forEach(id => playingIds.add(id));
              (g.team2_player_ids || []).forEach(id => playingIds.add(id));
            });
            const benchPlayers = players.filter(p => p.is_available && !playingIds.has(p.id));
            if (benchPlayers.length === 0 && openGames.length === 0) return null;
            return (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4" data-testid="bench-players-section">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Users size={12} /> On Bench <span className="text-slate-400 font-normal lowercase">({benchPlayers.length} player{benchPlayers.length !== 1 ? 's' : ''})</span>
                </h3>
                {benchPlayers.length === 0 ? (
                  <p className="text-xs text-slate-400">All available players are in active games</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {benchPlayers.map(p => {
                      const dname = p.display_name || p.name;
                      const trating = p.tournament_rating || p.doubles_rating || '';
                      return (
                        <div key={p.id} className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-full px-3 py-1" data-testid={`bench-player-${p.id}`}>
                          <div className="w-5 h-5 rounded-full bg-amber-200 flex items-center justify-center text-[10px] font-bold text-amber-700">
                            {dname?.[0]?.toUpperCase() || '?'}
                          </div>
                          <span className="text-xs font-medium text-amber-800">{dname}{trating ? ` (${trating})` : ''}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* Completed games grouped by court */}
          {completedGames.length > 0 && (
            <div>
              <h2 className="text-base font-bold text-slate-900 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Completed Games <span className="text-slate-400 font-normal">({completedGames.length})</span>
              </h2>
              <CourtGroupedGames games={completedGames} canManage={canManage} onScoreSaved={refreshGamesAndStandings} />
            </div>
          )}
        </div>
      )}

      {/* ── TAB: BRACKET ── */}
      {activeTab === 'Bracket' && (
        <TournamentBracket 
          games={games}
          players={players}
          standings={standings}
          gameType={tournament?.game_type}
        />
      )}

      {/* ── TAB: STANDINGS ── */}
      {activeTab === 'Standings' && (
        <div className="space-y-4">
          {/* Podium */}
          {standings.length >= 3 && (
            <div className="bg-gradient-to-r from-amber-50 via-yellow-50 to-amber-50 rounded-xl border border-amber-200 p-6" data-testid="tournament-podium">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-amber-600" />
                <h3 className="text-lg font-bold text-amber-800" style={{ fontFamily: 'Manrope, sans-serif' }}>Tournament Podium</h3>
              </div>
              <div className="flex items-end justify-center gap-4">
                {[1, 0, 2].map(rank => {
                  const s = standings[rank];
                  if (!s) return null;
                  const display = s.player_name || '?';
                  const initial = display[0]?.toUpperCase() || '?';
                  const heights = ['100px', '80px', '70px'];
                  const sizes = ['w-20 h-20 text-xl', 'w-16 h-16 text-lg', 'w-16 h-16 text-lg'];
                  const bgs = [
                    'bg-gradient-to-b from-amber-300 to-amber-400 shadow-md',
                    'bg-slate-200',
                    'bg-amber-200',
                  ];
                  const avatarBgs = ['bg-amber-100 border-amber-400', 'bg-slate-200 border-slate-300', 'bg-amber-100 border-amber-300'];
                  const textColors = ['text-white', 'text-slate-600', 'text-amber-700'];
                  const icons = [<Trophy key="t" className="w-5 h-5 text-white mx-auto mb-1" />, null, null];
                  return (
                    <div key={rank} className={`flex flex-col items-center ${rank === 0 ? '-mt-4' : ''}`}>
                      <div className={`${sizes[rank]} rounded-full ${avatarBgs[rank]} border-4 flex items-center justify-center font-bold mb-2 ${rank === 0 ? 'shadow-lg' : ''} ${rank === 0 ? 'text-amber-700' : rank === 1 ? 'text-slate-600' : 'text-amber-600'}`}>{initial}</div>
                      <div className={`${bgs[rank]} rounded-t-lg px-4 py-3 text-center`} style={{ height: heights[rank], minWidth: rank === 0 ? '120px' : '100px' }}>
                        {icons[rank]}
                        <p className={`text-xs font-bold ${textColors[rank]} leading-tight`} style={{ maxWidth: '110px', wordBreak: 'break-word' }}>{display}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="tournament-standings">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Tournament Standings</h2>
              <button onClick={refreshGamesAndStandings} className="text-slate-400 hover:text-green-600 p-1.5 hover:bg-slate-50 rounded-lg transition-colors" data-testid="refresh-standings-btn">
              <RefreshCw size={14} />
            </button>
          </div>
          {standings.length === 0 ? (
            <div className="py-12 text-center">
              <BarChart3 className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-400 text-sm">No standings yet. Complete some games to see rankings.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[9px]">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-100">
                    <th className="px-1 py-2 text-left font-semibold text-slate-500 uppercase w-5">#</th>
                    <th className="px-1 py-2 text-left font-semibold text-slate-500 uppercase">Player</th>
                    <th className="px-0.5 py-2 text-right font-semibold text-slate-500 uppercase">W/L</th>
                    <th className="px-0.5 py-2 text-right font-semibold text-slate-500 uppercase">W%</th>
                    <th className="px-0.5 py-2 text-right font-semibold text-slate-500 uppercase">G</th>
                    <th className="px-0.5 py-2 text-right font-semibold text-slate-500 uppercase">M</th>
                    <th className="px-0.5 py-2 text-right font-semibold text-slate-500 uppercase">PF</th>
                    <th className="px-0.5 py-2 text-right font-semibold text-slate-500 uppercase">PA</th>
                    <th className="px-0.5 py-2 text-right font-semibold text-slate-500 uppercase">±R</th>
                    <th className="px-0.5 py-2 text-right font-semibold text-slate-500 uppercase"><Trophy size={8} className="text-amber-500 inline" />T</th>
                    <th className="px-0.5 py-2 text-right font-semibold text-slate-500 uppercase"><Users size={8} className="text-blue-400 inline" />C</th>
                    <th className="px-0.5 py-2 text-right font-semibold text-slate-500 uppercase">Life</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((s, i) => {
                    const rd = s.rating_delta ?? (s.tournament_rating ? s.tournament_rating - 1500 : 0);
                    return (
                      <tr key={s.player_id} className={`${i < standings.length - 1 ? 'border-b border-slate-100' : ''} hover:bg-slate-50 transition-colors`} data-testid={`standing-row-${s.player_id}`}>
                        <td className="px-1 py-1.5 font-bold text-slate-900">{s.rank}</td>
                        <td className="px-1 py-1.5 max-w-[60px]">
                          <span className="font-medium text-slate-900 block truncate">{s.display_name || s.player_name}</span>
                        </td>
                        <td className="px-0.5 py-1.5 text-right whitespace-nowrap">
                          <span className="text-green-600 font-medium">{s.wins}</span>
                          <span className="text-slate-300 mx-px">/</span>
                          <span className="text-red-500 font-medium">{s.losses}</span>
                        </td>
                        <td className="px-0.5 py-1.5 text-right font-mono text-slate-600">
                          {s.win_pct != null ? `${s.win_pct > 1 ? Math.round(s.win_pct) : Math.round(s.win_pct * 100)}%` : '-'}
                        </td>
                        <td className="px-0.5 py-1.5 text-right font-mono text-slate-700">{s.games_played ?? 0}</td>
                        <td className="px-0.5 py-1.5 text-right font-mono text-red-400">{s.missed_games ?? 0}</td>
                        <td className="px-0.5 py-1.5 text-right font-mono text-slate-700">{s.points_for ?? s.pf ?? 0}</td>
                        <td className="px-0.5 py-1.5 text-right font-mono text-slate-700">{s.points_against ?? s.pa ?? 0}</td>
                        <td className="px-0.5 py-1.5 text-right font-medium">
                          <span className={rd >= 0 ? 'text-green-600' : 'text-red-500'}>{rd >= 0 ? '+' : ''}{rd}</span>
                        </td>
                        <td className="px-0.5 py-1.5 text-right">
                          <span className="font-mono font-bold text-amber-700 bg-amber-50 px-1 py-px rounded">{s.tournament_rating}</span>
                        </td>
                        <td className="px-0.5 py-1.5 text-right font-mono text-blue-700">{s.club_rating || s.group_rating || '-'}</td>
                        <td className="px-0.5 py-1.5 text-right font-mono text-slate-500">{s.lifetime_rating || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        
        {/* Audit Trail — visible to all club members */}
        <AuditTrailPanel tournamentId={id} />
        </div>
      )}

      {/* ── TAB: PLAYOFFS ── */}
      {activeTab === 'Playoffs' && (
        <PlayoffsTab tournamentId={id} tournament={tournament} canManage={canManage} onRefresh={fetchAll} />
      )}

      {showAddPlayers && (
        <AddPlayersModal
          tournamentId={id}
          existingPlayerIds={players.map(p => p.id)}
          groupId={selectedClub?.id}
          onClose={() => setShowAddPlayers(false)}
          onAdded={() => {
            api.get(`/tournaments/${id}/players`).then(r => setPlayers(r.data));
          }}
        />
      )}

      {showPartnerPairs && tournament && (
        <PartnerPairsModal
          tournament={tournament}
          players={players}
          onClose={() => setShowPartnerPairs(false)}
          onSaved={() => {
            setShowPartnerPairs(false);
            fetchAll();
          }}
        />
      )}
    </div>
  );
}
